'use client';

// A menu of actions under the button that opened it: "Add" asking what a
// speaker is for, one line per answer with what it will do.
//
// Dropdown picks a VALUE and shows it on its trigger; this runs an ACTION
// and closes. They share their mechanism (`useAnchoredMenu`: the body portal,
// placement, outside press, Esc) and their surface, so the two read as one
// family. Keyboard: the first choice takes focus on open, the arrows and
// Home/End move between choices, Enter or Space runs one, Esc and Tab leave.

import {
  useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { MENU_SURFACE, useAnchoredMenu } from './useAnchoredMenu';

export interface ActionMenuItem {
  key: string;
  label: string;
  /** What choosing it does, in a few words: "Replaces 2 speakers". */
  detail?: string;
  disabled?: boolean;
  onSelect: () => void;
}

export interface ActionMenuTriggerProps {
  ref: RefObject<HTMLButtonElement | null>;
  onClick: () => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
}

export default function ActionMenu({ trigger, items, title, label, testId, minWidth = 240 }: {
  /** The button that opens it, given the props that wire it up. */
  trigger: (props: ActionMenuTriggerProps) => ReactNode;
  items: readonly ActionMenuItem[];
  /** A line over the choices — the question they answer. */
  title?: string;
  /** The menu's accessible name. */
  label: string;
  /** The menu gets `${testId}-menu`, each choice `${testId}-${key}`. */
  testId?: string;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);
  const place = useAnchoredMenu(open, close, triggerRef, menuRef, { minWidth });

  const choices = () => [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];

  // The first choice takes focus once the menu is placed.
  useEffect(() => {
    if (place) choices()[0]?.focus({ preventScroll: true });
  }, [place]);

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = choices();
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    const to = e.key === 'ArrowDown' ? (i + 1) % list.length
      : e.key === 'ArrowUp' ? (i - 1 + list.length) % list.length
        : e.key === 'Home' ? 0
          : e.key === 'End' ? list.length - 1
            : -1;
    if (to >= 0 && list.length) {
      e.preventDefault();
      list[to].focus();
    } else if (e.key === 'Tab') {
      close();
    }
  };

  return (
    <>
      {trigger({
        ref: triggerRef,
        onClick: () => setOpen((v) => !v),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
      })}
      {place && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          data-testid={testId ? `${testId}-menu` : undefined}
          onKeyDown={onMenuKeyDown}
          style={{
            position: 'fixed',
            left: place.left,
            top: place.top,
            bottom: place.bottom,
            maxHeight: Math.max(120, place.maxHeight),
            minWidth: place.minWidth,
            zIndex: 1000,
          }}
          className={MENU_SURFACE}
        >
          {title && <p className="px-2.5 pb-1 pt-1.5 text-body-sm text-[var(--ed-text-dim)]">{title}</p>}
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              data-testid={testId ? `${testId}-${it.key}` : undefined}
              onClick={() => {
                close();
                it.onSelect();
              }}
              className={
                'flex w-full flex-col items-start rounded px-2.5 py-2 text-left transition-colors outline-none '
                + 'hover:bg-[var(--ed-surface-2)] focus-visible:bg-[var(--ed-surface-2)] '
                + 'disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent'
              }
            >
              <span className="text-body text-[var(--ed-text)]">{it.label}</span>
              {it.detail && <span className="text-body-sm text-[var(--ed-text-dim)]">{it.detail}</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
